import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { getHotelDisplayName } from '../utils/hotelValidation';
import LanguageSwitcher from './LanguageSwitcher';

// Top-level routes that are not hotel IDs
const NON_HOTEL_SEGMENTS = ['login', 'settings', 'shipping-settings', 'shipping-status', 'tokushoho', 'privacy', 'terms'];

export default function Navigation() {
  const { t } = useTranslation();
  const { currentUser, logout, userClaims } = useAuth();
  const navigate = useNavigate();
  const { hotelId } = useParams();
  const location = useLocation();
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const currentHotelId = pathSegments.length > 0 ? pathSegments[0] : null;
  const [hotelDisplayName, setHotelDisplayName] = useState('');

  // Fetch hotel display name when currentHotelId changes
  useEffect(() => {
    const fetchHotelDisplayName = async () => {
      if (currentHotelId && !NON_HOTEL_SEGMENTS.includes(currentHotelId)) {
        try {
          const displayName = await getHotelDisplayName(currentHotelId);
          setHotelDisplayName(displayName);
        } catch (error) {
          console.error('Error fetching hotel display name:', error);
          setHotelDisplayName(currentHotelId); // Fallback to hotel ID
        }
      } else {
        setHotelDisplayName('');
      }
    };

    fetchHotelDisplayName();
  }, [currentHotelId]);

  async function handleLogout() {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  }

  const getHotelLink = (path) => {
    const prefix = currentHotelId ? `/${currentHotelId}` : '';
    return `${prefix}${path}`;
  };

  // Check if user is staff-only (staff but not admin)
  const isStaffOnly = userClaims?.staff === true && userClaims?.admin !== true;
  const isAdmin = userClaims?.admin === true;

  // Check if we're on a hotel-specific page (has hotelId in URL)
  const isHotelSelected = Boolean(currentHotelId && !NON_HOTEL_SEGMENTS.includes(currentHotelId));

  const isShippingStatusAllowed = currentUser?.email?.toLowerCase() === 'daniel@quikbak.com';

  return (
    <nav className="navigation">
      <div className="nav-brand">
        <Link
          to={currentHotelId && isHotelSelected && currentUser ? (isStaffOnly ? `/${currentHotelId}/found-items/register` : `/${currentHotelId}/admin`) : '/'}
          style={{
            color: '#ffffff', /* White text */
            textDecoration: 'none', /* Remove underline */
            fontWeight: 'bold',
            fontSize: '1.2rem',
            textShadow: '1px 1px 2px rgba(0,0,0,0.2)', /* Subtle text shadow */
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <img
            src="/quikbakicon.png"
            alt="Quikbak Logo"
            style={{
              height: '45px',
              width: '45px',
              objectFit: 'contain'
            }}
          />
          {hotelDisplayName && isHotelSelected ? `${hotelDisplayName}` : ''}
        </Link>
      </div>
      <div className="nav-links">
        {currentUser ? (
          <>
            {isHotelSelected ? (
              // Only show navigation items when a hotel is selected
              <>
                {isStaffOnly ? (
                  // Staff-only users see limited navigation
                  <>
                    <Link to={getHotelLink('/found-items/register')}>{t('navigation.registerFoundItem')}</Link>
                  </>
                ) : (
                  // Admin and other users see full navigation
                  <>
                    <Link to={getHotelLink('/report-lost-item')}>{t('navigation.reportLost')}</Link>
                    <Link to={getHotelLink('/found-items/register')}>{t('navigation.registerFound')}</Link>
                    <Link to={getHotelLink('/admin')}>{t('navigation.dashboard')}</Link>
                    <Link to={getHotelLink('/settings')}>{t('navigation.settings')}</Link>
                  </>
                )}
              </>
            ) : null}
            {isShippingStatusAllowed && (
              <Link to="/shipping-status">{t('navigation.shippingStatus')}</Link>
            )}
            <LanguageSwitcher />
            <button onClick={handleLogout} className="logout-btn">{t('navigation.logout')}</button>
          </>
        ) : (
          <>
            <LanguageSwitcher />
            <Link to="/login">{t('navigation.staffLogin')}</Link>
          </>
        )}
      </div>
    </nav>
  );
} 
